import React, { Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { IndeedThemeProvider } from '@indeed/ifl-components';
import { aurora } from '@indeed/ifl-themes';
import { ProctorContextProvider } from '@indeed/frontend-proctor-utils';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client/core';
import { ApolloProvider } from '@apollo/client/react';
import { GettextInterface } from '@indeed/indeed-gettext';
import ClientErrorBoundary from './components/ErrorBoundary/ClientErrorBoundary';
import { InitialStateProvider, InitialState } from './lib/context/InitialStateContext';
import { I18nContext } from './util/GettextFactory';
import GlobalLinkNativeAppInterceptor from './components/GlobalLinkNativeAppInterceptor/GlobalLinkNativeAppInterceptor';
import { useRefetchOnPageResume } from './lib/hooks/useRefetchOnPageResume';

// ===== SHARED ROUTES =====
import PostApplyPage from './pages/post-apply/PostApplyPage';

// ===== LIGHT APPLY FLOW PAGES =====
import IndexPage from './pages/IndexPage';
import {
    LightApplyLayout,
    LightApplyIndexPage,
    NameInputPage,
    ContactDetailsPage,
    DateOfBirthPage,
    GenderPage,
    AddressPage,
    JapanResumePageWithSuspense,
    ScreenerQuestionsPage,
    ScoutSettingPage,
    JapanReviewPageWithSuspense,
    LightApplyPostApplyPage,
} from './pages/light-apply';

// ===== DEFAULT APPLY FLOW PAGES =====
import {
    DefaultApplyLayout,
    DefaultApplyIndexPage,
    ContactInfoPage,
    ProfilePicturePage,
    ResumePage,
    WorkExperiencePage,
    QuestionsPage,
    DocumentsPage,
    ReviewPage,
} from './pages/default-apply';

// ===== AUTO APPLY SETUP FLOW PAGES =====
import {
    AutoApplySetupRoot,
    AutoApplySetupLayout,
    AutoApplySetupIndexPage,
    AutomationLevelPage,
    ResumePage as AutoApplyResumePage,
    PastAnswersPage,
    JobPreferencesPage,
    ReviewPage as AutoApplyReviewPage,
    CompletePage as AutoApplySetupCompletePage,
    AutoApplyTerms,
    ReportIssuePage,
    CategorySelectionStep,
    DetailsFormStep,
    ValidCategoryGuard,
    MidSetupFeedbackPage,
} from './pages/auto-apply-setup';

// ===== AUTO APPLY FLOW PAGES =====
import {
    AutoApplyRoot,
    AutoApplyLayout,
    AutoApplyIndexPage,
    ReviewPage as AutoApplyFlowReviewPage,
    ContactInfoPage as AutoApplyFlowContactInfoPage,
    ResumePage as AutoApplyFlowResumePage,
    QuestionsPage as AutoApplyFlowQuestionsPage,
    ConfirmationPage,
    SuccessPage,
    ApplyForMePromoPage,
} from './pages/auto-apply';
import type { EventLogger } from './lib/eventLogger/eventLogger.client';
import { EventLoggerProvider } from './lib/eventLogger/EventLoggerProvider';
import { useParams } from 'react-router-dom';

// ==== TEST PAGES ====
import TestRbsPage from './pages/test/TestRbsPage';
import TestSetupPage from './pages/test/TestSetupPage';

/**
 * Renders children only after client-side mount.
 * Prevents SSR from rendering lazy/Suspense boundaries that renderToString
 * can't handle, avoiding the hydration mismatch warning.
 */
const ClientOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [mounted, setMounted] = React.useState(false);
    React.useEffect(() => {
        setMounted(true);
    }, []);
    return mounted ? <>{children}</> : null;
};

/**
 * Higher-order component that forces remounting when a route parameter changes.
 * Useful for resetting component state when navigating between different IDs.
 *
 * @param Component - The component to wrap
 * @param paramName - The route parameter name to use as key (e.g., 'draftId', 'userId')
 * @returns Wrapped component that remounts on param change
 *
 * @example
 * const ReviewPageWrapper = withParamKey(ReviewPage, 'draftId');
 * <Route path=":draftId/review" element={<ReviewPageWrapper />} />
 */
const withParamKey = <P extends object>(
    Component: React.ComponentType<P>,
    paramName: string,
): React.FC<P> => {
    return (props: P) => {
        const params = useParams();
        const key = params[paramName];
        return <Component {...props} key={key} />;
    };
};

// Apply to ReviewPage to reset state when navigating between drafts
const AutoApplyFlowReviewPageWrapper = withParamKey(AutoApplyFlowReviewPage, 'draftId');

// QuestionsPage seeds answer state from the draft's questions, so it must remount per draft
const AutoApplyFlowQuestionsPageWrapper = withParamKey(AutoApplyFlowQuestionsPage, 'draftId');

/**
 * Flags the route tree branches on, passed in so a caller that only wants the paths can evaluate
 * both sides (IAFE-2300).
 */
export interface AppRouteFlags {
    /** apply_for_me_sfm_tog is off, which hides the automation-level steps. */
    isApplyForMeSfmDisabled: boolean;
}

/**
 * The route tree, kept outside the component so it can be read without rendering anything.
 * `createRoutesFromElements` turns it into the same route objects `<Routes>` builds internally.
 * src/server/utils/appRouteCoverage.test.ts uses that to check the Datadog route list against
 * the real routes.
 */
export const buildAppRouteElements = ({
    isApplyForMeSfmDisabled,
}: AppRouteFlags): React.ReactElement => (
    <>
        <Route
            path="/test/rbs"
            element={
                <ClientOnly>
                    <Suspense fallback={null}>
                        <TestRbsPage />
                    </Suspense>
                </ClientOnly>
            }
        />
        <Route
            path="/test/setup"
            element={
                <ClientOnly>
                    <Suspense fallback={null}>
                        <TestSetupPage />
                    </Suspense>
                </ClientOnly>
            }
        />
        {/* Auto Apply Setup - Root handles event logging for all sub-routes */}
        <Route path="/auto-apply-setup" element={<AutoApplySetupRoot />}>
            <Route path="terms" element={<AutoApplyTerms />} />
            {/* Report Issue Flow - Path param based routing */}
            <Route path="report-issue" element={<ReportIssuePage />}>
                <Route index element={<CategorySelectionStep />} />
                <Route
                    path=":category"
                    element={
                        <ValidCategoryGuard>
                            <DetailsFormStep />
                        </ValidCategoryGuard>
                    }
                />
            </Route>
            {/* Mid-Setup Feedback Flow (IAFE-880) */}
            <Route path="mid-setup-feedback/*" element={<MidSetupFeedbackPage />} />
            {/* Past Answers Page - Standalone page */}
            <Route path="past-answers" element={<PastAnswersPage />} />
            {/* Auto Apply Setup Flow - Layout provides visual structure */}
            <Route element={<AutoApplySetupLayout />}>
                <Route index element={<AutoApplySetupIndexPage />} />
                {!isApplyForMeSfmDisabled && (
                    <Route path="automation-level" element={<AutomationLevelPage />} />
                )}
                {!isApplyForMeSfmDisabled && (
                    <Route path="automation-level/edit" element={<AutomationLevelPage />} />
                )}
                <Route path="resume" element={<AutoApplyResumePage />} />
                <Route path="resume/edit" element={<AutoApplyResumePage />} />
                <Route path="job-preferences" element={<JobPreferencesPage />} />
                <Route path="review" element={<AutoApplyReviewPage />} />
                {/* Entry-driven Review variants (IAFE-2081):
                    review-before-submit (homepage) and
                    apply-for-me (MyJobs/promo). Same component
                    and flow step; the path drives the disclaimer
                    and button behavior. */}
                <Route path="review/:reviewVariant" element={<AutoApplyReviewPage />} />
                <Route path="complete" element={<AutoApplySetupCompletePage />} />
                <Route path="*" element={<AutoApplySetupIndexPage />} />
            </Route>
        </Route>

        {/* Auto Apply Flow - Root handles proctor gating and event logging */}
        <Route path="/auto-apply" element={<AutoApplyRoot />}>
            {/* Index route: fetches drafts, redirects to first draft */}
            <Route index element={<AutoApplyIndexPage />} />
            {/* Apply For Me promo: loaded bare inside a native
                JSMABridge webview modal (no AutoApplyLayout chrome) */}
            <Route path="apply-for-me-promo" element={<ApplyForMePromoPage />} />
            {/* Layout is nested: provides visual structure only */}
            <Route element={<AutoApplyLayout />}>
                {/* Per-draft pages with dynamic draftId */}
                <Route path=":draftId">
                    <Route path="review" element={<AutoApplyFlowReviewPageWrapper />} />
                    {/* Progressive-automation review detour
                        (rnhp_auto_apply_banner_tst value 2):
                        opened from the confirmation page as a
                        sub-flow; renders the same ReviewPage
                        with a single "Update" CTA. */}
                    <Route path="review/edit" element={<AutoApplyFlowReviewPageWrapper />} />
                    <Route path="contact-info" element={<AutoApplyFlowContactInfoPage />} />
                    <Route path="contact-info/edit" element={<AutoApplyFlowContactInfoPage />} />
                    <Route path="resume" element={<AutoApplyFlowResumePage />} />
                    <Route path="resume/edit" element={<AutoApplyFlowResumePage />} />
                    <Route path="questions" element={<AutoApplyFlowQuestionsPageWrapper />} />
                    <Route path="questions/edit" element={<AutoApplyFlowQuestionsPageWrapper />} />
                </Route>
                {/* Batch confirmation and success */}
                <Route path="confirmation" element={<ConfirmationPage />} />
                <Route path="success" element={<SuccessPage />} />
            </Route>
        </Route>

        {/* Main Entry Point */}
        <Route path="/" element={<IndexPage />}>
            {/* Default Application Flow */}
            <Route path="/default-apply" element={<DefaultApplyLayout />}>
                <Route index element={<DefaultApplyIndexPage />} />
                <Route path="contact-info" element={<ContactInfoPage />} />
                <Route path="profile-picture" element={<ProfilePicturePage />} />
                <Route path="resume" element={<ResumePage />} />
                <Route path="work-experience" element={<WorkExperiencePage />} />
                <Route path="questions" element={<QuestionsPage />} />
                <Route path="documents" element={<DocumentsPage />} />
                <Route path="review" element={<ReviewPage />} />
                <Route path="*" element={<DefaultApplyIndexPage />} />
            </Route>

            {/* Light Apply Flow */}
            <Route path="/light-apply" element={<LightApplyLayout />}>
                <Route index element={<LightApplyIndexPage />} />
                <Route path="contact-info/1" element={<NameInputPage />} />
                <Route path="contact-info/2" element={<ContactDetailsPage />} />
                <Route path="contact-info/3" element={<DateOfBirthPage />} />
                <Route path="contact-info/4" element={<GenderPage />} />
                <Route path="contact-info/5" element={<AddressPage />} />
                <Route path="contact-info" element={<NameInputPage />} />
                <Route path="resume" element={<JapanResumePageWithSuspense />} />
                <Route path="questions" element={<ScreenerQuestionsPage />} />
                <Route path="scout-setting" element={<ScoutSettingPage />} />
                <Route path="review" element={<JapanReviewPageWithSuspense />} />
                <Route path="post-apply" element={<LightApplyPostApplyPage />} />
                <Route path="*" element={<LightApplyIndexPage />} />
            </Route>

            {/* Shared Routes */}
            <Route path="/post-apply" element={<PostApplyPage />} />
        </Route>

        {/* Fallback - redirect to main entry point */}
        <Route path="*" element={<IndexPage />} />
    </>
);

export interface AppProps {
    initialState: InitialState;
    apolloClient: ApolloClient<NormalizedCacheObject>;
    gt: GettextInterface;
    Router: React.ComponentType<{ children: React.ReactNode }>;
    eventLogger?: EventLogger;
}

const App: React.FunctionComponent<AppProps> = ({
    initialState,
    apolloClient,
    gt,
    Router,
    eventLogger,
}) => {
    const proctorGroups: Partial<InitialState['proctorGroups']> = initialState.proctorGroups;
    const isApplyForMeSfmDisabled = proctorGroups.apply_for_me_sfm_tog?.value === 0;

    // Keep every route in sync when the user returns to the page after data was
    // changed on another surface — via tab/webview refocus (IAFE-2525) or bfcache
    // restore on browser Back (IAFE-1455). Called here at the App root — above
    // ApolloProvider — so it covers all routes; the client is passed explicitly.
    useRefetchOnPageResume(apolloClient);

    return (
        <ApolloProvider client={apolloClient}>
            <I18nContext.Provider value={gt}>
                <InitialStateProvider initialState={initialState}>
                    <ProctorContextProvider proctorGroups={initialState.proctorGroups}>
                        <IndeedThemeProvider
                            theme={aurora}
                            useIdProviderProps={{
                                namespace: 'auto-apply-preference',
                            }}
                        >
                            <EventLoggerProvider eventLogger={eventLogger}>
                                <Router>
                                    <Helmet>
                                        <title>Auto Apply Preference - Indeed</title>
                                        <meta
                                            name="description"
                                            content="Apply for exciting opportunities at Indeed. Complete your application in a few simple steps."
                                        />
                                        <meta
                                            name="viewport"
                                            content="width=device-width, initial-scale=1"
                                        />
                                    </Helmet>
                                    <ClientErrorBoundary>
                                        {/* Native app webview link interceptor (IAFE-235) */}
                                        <GlobalLinkNativeAppInterceptor />
                                        <Routes>
                                            {buildAppRouteElements({ isApplyForMeSfmDisabled })}
                                        </Routes>
                                    </ClientErrorBoundary>
                                </Router>
                            </EventLoggerProvider>
                        </IndeedThemeProvider>
                    </ProctorContextProvider>
                </InitialStateProvider>
            </I18nContext.Provider>
        </ApolloProvider>
    );
};

export default App;
